import { useTranslation } from 'react-i18next';

type ErisStatusResultsTablePaginationHeaderProps = {
  page: number;
  perPage: number;
  total: number;
  onPageChange: (page: number) => void;
  onPerPageChange: (perPage: number) => void;
};

const PAGINATION_RULES = {
  SHOW_ALL_THRESHOLD: 7,
  START_END_BUFFER: 4,
};

const navigationButtonClassName = `
  p-1 text-sm font-semibold text-blue-600
  hover:underline
  disabled:text-gray-400 disabled:no-underline
  visited:text-purple-600
`;

export const ErisStatusResultsTablePaginationHeader: React.FC<
  ErisStatusResultsTablePaginationHeaderProps
> = ({ page, perPage, total, onPageChange }) => {
  const lastPage = Math.max(1, Math.ceil(total / perPage));
  const { t } = useTranslation();
  const getPages = () => {
    if (lastPage <= PAGINATION_RULES.SHOW_ALL_THRESHOLD) {
      return Array.from({ length: lastPage }, (_, i) => i + 1);
    }

    const pages: (number | string)[] = [1];
    if (page <= PAGINATION_RULES.START_END_BUFFER) {
      pages.push(2, 3, 4, 5, '...', lastPage);
    } else if (page >= lastPage - 3) {
      pages.push('...', lastPage - 4, lastPage - 3, lastPage - 2, lastPage - 1, lastPage);
    } else {
      pages.push('...', page - 1, page, page + 1, '...', lastPage);
    }

    return pages;
  };

  const pages = getPages();

  return (
    <div className=" flex flex-1 flex-row justify-between">
      <div>
        <span>
          {`${t('eris.supportStatusList.all')} ${total} ${total > 1 ? t('eris.supportStatusList.items') : t('eris.supportStatusList.item')} ＜${page}/${lastPage}＞`}
        </span>
      </div>
      <div className="flex flex-row items-center space-x-4">
        <button
          className={navigationButtonClassName}
          onClick={() => onPageChange(Math.max(1, page - 1))}
          disabled={page === 1}
        >
          {t('eris.supportStatusList.previous')}
        </button>
        <div className="flex space-x-1">
          {pages.map((p, i) =>
            p === '...' ? (
              <span key={i} className="px-1 text-gray-500">
                ...
              </span>
            ) : (
              <button
                key={i}
                className={`rounded px-2 py-1 text-sm font-semibold ${
                  p === page ? 'bg-gray-300 text-gray-700' : 'text-gray-600 hover:underline'
                }`}
                onClick={() => onPageChange(p as number)}
              >
                {p}
              </button>
            )
          )}
        </div>
        <button
          className={navigationButtonClassName}
          onClick={() => onPageChange(Math.min(lastPage, page + 1))}
          disabled={page === lastPage}
        >
          {t('eris.supportStatusList.next')}
        </button>
      </div>
    </div>
  );
};
